<template>
    <div
        class="top_container"
        :class="{
            'top_container--sticky': sticky,
            'top_container--inline': !sticky,
            'top_container--full_width': fullWidth,
        }"
    >
        <div class="button_group">
            <Button
                class="submit_button"
                :disabled="disabled"
                @click="dontDisable ? handleSubmit($event) : submitAndDisable($event)"
            >
                <span class="submit_button__content" :class="{ 'submit_button__content--hidden': loading }">
                    <span>{{ completeText ?? 'Complete step' }}</span>
                    <!-- The arrow marks wizard steps; single-step actions
                         (Save, Confirm) opt out by omitting `arrow`. -->
                    <i v-if="arrow" class="i-arrow-right text-inherit" />
                </span>
                <i v-if="loading" class="submit_button__spinner i-spinner-white animate-spin" />
            </Button>
        </div>

        <div class="button_group">
            <slot> </slot>
        </div>

        <div v-if="!noCancel" class="button_group">
            <button @click="$emit('cancel')">
                <span>{{ cancelText ?? 'Cancel' }}</span>
                <i class="i-close" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { ref } from 'vue';

const props = withDefaults(
    defineProps<{
        handleSubmit: (payload: MouseEvent) => void;
        noCancel?: boolean;
        cancelText?: string;
        completeText?: string;
        disabled?: boolean;
        fullWidth?: boolean;
        sticky?: boolean;
        dontDisable?: boolean;
        loading?: boolean;
        /** Show the wizard-step arrow on the primary button. */
        arrow?: boolean;
    }>(),
    {
        sticky: true,
    },
);

const submitted = ref(false);

const submitAndDisable = (event: MouseEvent) => {
    props.handleSubmit(event);
    submitted.value = true;
    setTimeout(() => {
        submitted.value = false;
    }, 1500);
};

defineEmits(['cancel']);
</script>

<style scoped>
@reference '@/assets/styles/default.css';

.top_container {
    display: flex;
    gap: var(--spacing-md);
    padding: var(--spacing-sm) var(--spacing-md);
    z-index: 10;

    background-color: var(--surface-1);
}

.top_container--sticky {
    /* The bar's separator is a divider, so it takes the divider role token —
       the old --bg-primary read near-black in dark mode. */
    border-top: 1px solid var(--border-subtle);
    position: fixed;
    bottom: 0;
    width: calc(8 / 12 * 100%);
}

.top_container--inline {
    margin-top: var(--spacing-xl);
}

/* Below the phone breakpoint the page gutter is --spacing-xs; the bar's
   buttons line up with the page content instead of sitting 32px in. */
@media (max-width: 767px) {
    .top_container {
        padding-inline: var(--spacing-xs);
    }
}

.submit_button {
    position: relative;
}

.submit_button__content {
    display: flex;
    align-items: center;
    justify-content: center;
}

.submit_button__content--hidden {
    visibility: hidden;
}

.submit_button__spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.top_container--full_width {
    left: 0;
    right: 0;
    width: auto;
}

@media (min-width: 1024px) {
    .top_container--full_width {
        left: var(--desktop-sidebar-width, 0);
    }
}
</style>
